// src/hooks/useWallet.js
import { useState, useEffect } from "react";
import { ethers } from "ethers";
import { EthereumProvider } from "@walletconnect/ethereum-provider";

// 🔐 Your WalletConnect Project ID (keep this secret in production)
const PROJECT_ID = "b6f608c79094519d197360409f0187b4";

// 🌐 Use Mainnet
const CHAIN_ID = 1;

export default function useWallet() {
  const [wallet, setWallet] = useState(null);
  const [signer, setSigner] = useState(null);
  const [provider, setProvider] = useState(null);
  const [wcProvider, setWcProvider] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);

  // Detect if we're in a mobile wallet browser
  const detectWalletBrowser = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // Check for specific wallet browsers
    if (window.ethereum) {
      if (window.ethereum.isMetaMask) return "MetaMask";
      if (window.ethereum.isTrust) return "Trust Wallet";
      if (window.ethereum.isRainbow) return "Rainbow";
      if (window.ethereum.isCoinbaseWallet) return "Coinbase Wallet";
    }
    
    // Check user agent for wallet browsers
    if (/Trust/i.test(userAgent)) return "Trust Wallet";
    if (/Rainbow/i.test(userAgent)) return "Rainbow";
    if (/CoinbaseWallet/i.test(userAgent)) return "Coinbase Wallet";
    
    return null;
  };

  const connectWallet = async () => {
    if (isConnecting) return; // Prevent multiple connection attempts
    setIsConnecting(true);

    try {
      // ✅ Check if we're in a wallet's browser or have an injected provider
      if (window.ethereum) {
        const walletType = detectWalletBrowser();
        console.log("🔍 Detected wallet:", walletType || "Unknown");

        const _provider = new ethers.providers.Web3Provider(window.ethereum);
        
        // Request account access
        await _provider.send("eth_requestAccounts", []);
        
        // Check if we're on the correct network
        const network = await _provider.getNetwork();
        if (network.chainId !== CHAIN_ID) {
          try {
            await window.ethereum.request({
              method: 'wallet_switchEthereumChain',
              params: [{ chainId: `0x${CHAIN_ID.toString(16)}` }],
            });
          } catch (switchError) {
            // This error code indicates that the chain has not been added to MetaMask
            if (switchError.code === 4902) {
              alert("Please add Ethereum Mainnet to your wallet");
              setIsConnecting(false);
              return;
            }
            throw switchError;
          }
        }

        const _signer = _provider.getSigner();
        const address = await _signer.getAddress();

        setProvider(_provider);
        setSigner(_signer);
        setWallet(address);
        setWcProvider(null); // Not using WalletConnect in this case
        localStorage.setItem("walletConnected", "true");
        localStorage.setItem("walletType", "injected");
        
        console.log("✅ Connected via injected provider:", address);
        setIsConnecting(false);
        return;
      }

      // ✅ WalletConnect for QR code connection (Desktop or Mobile without wallet browser)
      console.log("🔄 No injected provider found, using WalletConnect...");
      
      const _wcProvider = await EthereumProvider.init({
        projectId: PROJECT_ID,
        chains: [CHAIN_ID],
        showQrModal: true,
        qrModalOptions: {
          themeMode: "dark",
          themeVariables: {
            "--wcm-z-index": "999999",
          },
        },
      });

      await _wcProvider.connect();
      
      const _provider = new ethers.providers.Web3Provider(_wcProvider);
      const _signer = _provider.getSigner();
      const address = await _signer.getAddress();

      setProvider(_provider);
      setSigner(_signer);
      setWallet(address);
      setWcProvider(_wcProvider);
      localStorage.setItem("walletConnected", "true");
      localStorage.setItem("walletType", "walletconnect");

      console.log("✅ Connected via WalletConnect:", address);

      // Listen for WalletConnect events
      _wcProvider.on("disconnect", () => {
        console.log("WalletConnect disconnected");
        disconnectWallet();
      });

      // ✅ Try to hide the WalletConnect modal after connection
      setTimeout(() => {
        const modal = document.querySelector("wcm-modal");
        if (modal) modal.style.display = "none";
      }, 1000);

    } catch (err) {
      console.error("❌ Wallet connection failed:", err);
      
      // User-friendly error messages
      if (err.code === 4001 || err.message?.includes("rejected")) {
        alert("Connection cancelled. Please try again when ready.");
      } else if (err.message?.includes("Modal closed")) {
        // User closed WalletConnect modal
        console.log("WalletConnect modal closed by user");
      } else {
        alert("⚠️ Wallet connection failed. Please try again.");
      }
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectWallet = async () => {
    setWallet(null);
    setSigner(null);
    setProvider(null);
    localStorage.removeItem("walletConnected");
    localStorage.removeItem("walletType");

    if (wcProvider?.disconnect) {
      try {
        await wcProvider.disconnect();
        setWcProvider(null);
      } catch (err) {
        console.warn("Disconnect failed:", err);
      }
    }
  };

  // Auto-reconnect on page load
  useEffect(() => {
    const autoReconnect = async () => {
      const wasConnected = localStorage.getItem("walletConnected") === "true";
      const walletType = localStorage.getItem("walletType");
      
      if (!wasConnected) return;

      // Only auto-reconnect if we have an injected provider
      if (walletType === "injected" && window.ethereum) {
        try {
          const accounts = await window.ethereum.request({ method: 'eth_accounts' });
          if (accounts.length > 0) {
            await connectWallet();
          } else {
            // Wallet is locked or permission was revoked
            localStorage.removeItem("walletConnected");
            localStorage.removeItem("walletType");
          }
        } catch (err) {
          console.warn("Auto-reconnect failed:", err);
        }
      }
      // Don't auto-reconnect WalletConnect sessions
    };
    
    autoReconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for account changes
  useEffect(() => {
    if (!window.ethereum) return;

    const handleAccountsChanged = (accounts) => {
      if (accounts.length === 0) {
        // User disconnected wallet
        disconnectWallet();
      } else if (accounts[0] !== wallet && wallet) {
        // User switched accounts
        window.location.reload();
      }
    };

    const handleChainChanged = () => {
      // Reload on chain change
      window.location.reload();
    };

    window.ethereum.on('accountsChanged', handleAccountsChanged);
    window.ethereum.on('chainChanged', handleChainChanged);

    return () => {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', handleChainChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet]);

  return {
    wallet,
    signer,
    provider,
    wcProvider, // ✅ Now properly exposed
    connectWallet,
    disconnectWallet,
    isConnecting,
    detectWalletBrowser,
  };
}